export const POSITIONAL_RE = /\{\{(\d+)\}\}/g;
export const NAMED_RE = /\{\{([a-z][a-z0-9_]*)\}\}/g;
export const VAR_TOKEN_RE = /\{\{([^}]+)\}\}/g;

/**
 * Scan a single text string for variable tokens.
 * Returns { mode: 'positional'|'named'|'none'|'mixed', tokens: string[], errors: string[] }
 */
export function detectVariables(text) {
  if (!text) return { mode: 'none', tokens: [], errors: [] };

  const allTokens = [];
  const errors = [];

  let m;
  VAR_TOKEN_RE.lastIndex = 0;
  // eslint-disable-next-line no-cond-assign
  while ((m = VAR_TOKEN_RE.exec(text)) !== null) {
    allTokens.push(m[1]);
  }

  if (allTokens.length === 0) return { mode: 'none', tokens: [], errors: [] };

  const positional = [];
  const named = [];

  allTokens.forEach(token => {
    if (/^\d+$/.test(token)) {
      positional.push(token);
    } else if (/^[a-z][a-z0-9_]*$/.test(token)) {
      named.push(token);
    } else {
      errors.push(`Invalid variable token: {{${token}}}`);
    }
  });

  const validTokens = [...positional, ...named];
  let mode;
  if (positional.length > 0 && named.length > 0) {
    mode = 'mixed';
    errors.push('Template mixes positional ({{1}}) and named ({{name}}) variables.');
  } else if (positional.length > 0) {
    mode = 'positional';
  } else if (named.length > 0) {
    mode = 'named';
  } else {
    mode = 'none';
  }

  return { mode, tokens: validTokens, errors };
}

/**
 * Determine the overall variable mode of a template by inspecting BODY + HEADER only.
 * URL buttons always use positional {{1}} independently — excluded from mode detection.
 * Returns { mode: 'positional'|'named'|'none'|'mixed', errors: string[] }
 */
export function detectTemplateMode({ bodyText, headerText }) {
  const bodyResult = detectVariables(bodyText || '');
  const headerResult = detectVariables(headerText || '');

  const errors = [...bodyResult.errors, ...headerResult.errors];

  const modes = new Set(
    [bodyResult.mode, headerResult.mode].filter(mode => mode !== 'none')
  );

  let mode;
  if (modes.size === 0) {
    mode = 'none';
  } else if (modes.size === 1) {
    [mode] = [...modes];
  } else {
    mode = 'mixed';
    errors.push('Template mixes positional and named variables across BODY and HEADER.');
  }

  return { mode, errors };
}

/**
 * Validate that positional tokens form a sequence 1,2,3,... without gaps.
 * Repetitions are allowed (Meta accepts {{1}} multiple times in the same component).
 * Returns { valid: boolean, error?: string }
 */
export function validatePositionalSequence(tokens) {
  if (!tokens || tokens.length === 0) return { valid: true };

  const nums = [...new Set(tokens.map(Number))].sort((a, b) => a - b);

  if (nums[0] !== 1) {
    return { valid: false, error: 'Positional variables must start at 1.' };
  }

  const gap = nums.find((n, i) => i > 0 && n !== nums[i - 1] + 1);
  if (gap !== undefined) {
    const prev = nums[nums.indexOf(gap) - 1];
    return {
      valid: false,
      error: `Positional variables have a gap: missing {{${prev + 1}}}.`,
    };
  }

  return { valid: true };
}

/**
 * Validate a named variable identifier.
 * Rules: lowercase letters, digits, underscore; starts with lowercase letter; max 32 chars.
 */
export function isValidNamedVar(name) {
  if (!name || name.length > 32) return false;
  return /^[a-z][a-z0-9_]*$/.test(name);
}
